"use client"

import { useEffect, useState } from "react"
import { Tag, Clock, TrendingDown } from "lucide-react"
import { Promotion, Product } from "@/lib/types"
import { formatPrice } from "@/lib/utils"
import Image from "next/image"
import Link from "next/link"

interface PromotionsBlockProps {
    apiUrl?: string
}

export function PromotionsBlock({ apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5000" }: PromotionsBlockProps) {
    const [promotions, setPromotions] = useState<Promotion[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        fetchActivePromotions()
        const interval = setInterval(fetchActivePromotions, 60000) // Refresh every minute
        return () => clearInterval(interval)
    }, [])

    const fetchActivePromotions = async () => {
        try {
            const response = await fetch(`${apiUrl}/promotions/active`)
            const data = await response.json()
            if (data.success) {
                setPromotions(data.promotions.slice(0, 6)) // Limit to 6 promotions
            }
        } catch (error) {
            console.error("Error fetching promotions:", error)
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <section className="py-16 bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 dark:from-red-950/20 dark:via-orange-950/20 dark:to-yellow-950/20">
                <div className="container">
                    <div className="text-center">
                        <p className="text-foreground-secondary dark:text-dark-foreground-secondary">Chargement des promotions...</p>
                    </div>
                </div>
            </section>
        )
    }

    if (promotions.length === 0) {
        return null
    }

    return (
        <section className="py-16 bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 dark:from-red-950/20 dark:via-orange-950/20 dark:to-yellow-950/20">
            <div className="container">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-full mb-4 animate-pulse">
                        <Tag size={20} />
                        <span className="font-bold">PROMOTIONS EN COURS</span>
                    </div>
                    <h2 className="text-4xl font-bold mb-4 text-foreground dark:text-dark-foreground">
                        Offres à <span className="text-red-600 dark:text-red-400">Durée Limitée</span>
                    </h2>
                    <p className="text-foreground-secondary dark:text-dark-foreground-secondary text-lg">
                        Profitez de nos meilleures offres avant qu'il ne soit trop tard!
                    </p>
                </div>

                {/* Promotions Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {promotions.map((promotion) => (
                        <PromotionCard key={promotion._id} promotion={promotion} />
                    ))}
                </div>
            </div>
        </section>
    )
}

interface PromotionCardProps {
    promotion: Promotion
}

function PromotionCard({ promotion }: PromotionCardProps) {
    const product = promotion.productId as Product
    const [timeLeft, setTimeLeft] = useState("")

    useEffect(() => {
        const calculateTimeLeft = () => {
            const now = new Date().getTime()
            const end = new Date(promotion.endDate).getTime()
            const distance = end - now

            if (distance < 0) {
                setTimeLeft("Expiré")
                return
            }

            const days = Math.floor(distance / (1000 * 60 * 60 * 24))
            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
            const seconds = Math.floor((distance % (1000 * 60)) / 1000)

            if (days > 0) {
                setTimeLeft(`${days}j ${hours}h ${minutes}m`)
            } else if (hours > 0) {
                setTimeLeft(`${hours}h ${minutes}m ${seconds}s`)
            } else {
                setTimeLeft(`${minutes}m ${seconds}s`)
            }
        }

        calculateTimeLeft()
        const interval = setInterval(calculateTimeLeft, 1000)
        return () => clearInterval(interval)
    }, [promotion.endDate])

    const originalPrice = product?.price || 0
    const discountedPrice =
        promotion.discountType === "PERCENTAGE"
            ? originalPrice - (originalPrice * promotion.discountValue) / 100
            : originalPrice - promotion.discountValue

    const discountPercentage =
        promotion.discountType === "PERCENTAGE"
            ? promotion.discountValue
            : Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)

    return (
        <Link
            href={`/produits/${product?._id || product?.id}`}
            className="group relative bg-white dark:bg-dark-card rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-red-200 dark:border-red-900/50"
        >
            {/* Discount Badge */}
            <div className="absolute top-4 left-4 z-10">
                {promotion.discountType === "PERCENTAGE" && <div className="bg-red-600 text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg flex items-center gap-2">
                    <TrendingDown size={20} />
                    -{discountPercentage}%
                </div>}
            </div>

            {/* Product Image */}
            <div className="relative h-64 bg-background-secondary dark:bg-primary-dark/10 overflow-hidden">
                {product?.images?.[0] ? (
                    <Image
                        src={product.images[0]}
                        alt={product.name}
                        fill
                        unoptimized
                        className="object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                ) : (
                    <Image
                        src="offre_speciale.jpg"
                        alt={product?.name || "Produit"}
                        fill
                        unoptimized
                        className="object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                )}
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
                {/* Product Name */}
                <h3 className="text-xl font-bold text-foreground dark:text-dark-foreground line-clamp-2 group-hover:text-primary transition-colors">
                    {product?.name || "Produit"}
                </h3>

                {/* Promotion Name */}
                <p className="text-sm text-red-600 dark:text-red-400 font-semibold">{promotion.name}</p>

                {/* Prices */}
                <div className="space-y-1">
                    <div className="flex items-baseline gap-3">
                        <span className="text-3xl font-bold text-red-600 dark:text-red-400">{formatPrice(discountedPrice)}</span>
                        <span className="text-lg text-foreground-secondary dark:text-dark-foreground-secondary line-through">
                            {formatPrice(originalPrice)}
                        </span>
                    </div>
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                        Économisez {formatPrice(originalPrice - discountedPrice)} {promotion.applicationType === "GENERAL" ? "sur tous les produits" : ""}{promotion.applicationType === "CATEGORY" ? ` dans la catégorie ${promotion.categoryId}` : ""}
                    </p>
                </div>

                {/* Countdown Timer */}
                <div className="bg-gradient-to-r from-red-100 to-orange-100 dark:from-red-950/30 dark:to-orange-950/30 rounded-lg p-4 border-2 border-red-300 dark:border-red-800">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                            <Clock size={20} className="animate-pulse" />
                            <span className="font-semibold">Se termine dans:</span>
                        </div>
                    </div>
                    <div className="mt-2 text-center">
                        <span className="text-2xl font-bold text-red-600 dark:text-red-400 font-mono">{timeLeft}</span>
                    </div>
                </div>

                {/* Stock Info */}
                {product?.stock !== undefined && product?.stock !== null && (
                    <div className="text-sm text-foreground-secondary dark:text-dark-foreground-secondary">
                        {product.stock > 0 ? (
                            <span className="text-green-600 dark:text-green-400">✓ En stock ({product.stock} disponibles)</span>
                        ) : (
                            <span className="text-red-600 dark:text-red-400">✗ Rupture de stock</span>
                        )}
                    </div>
                )}

                {/* Usage Limit Info */}
                {promotion.maxUsageCount && (
                    <div className="bg-white dark:bg-dark-background rounded-lg p-3 border border-border dark:border-dark-border">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-foreground-secondary dark:text-dark-foreground-secondary">
                                Places restantes:
                            </span>
                            <span className="text-sm font-bold text-primary dark:text-primary-light">
                                {Math.max(promotion.maxUsageCount - promotion.currentUsageCount, 0)} / {promotion.maxUsageCount}
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                                className={`h-2 rounded-full transition-all ${promotion.currentUsageCount >= promotion.maxUsageCount
                                    ? "bg-red-500"
                                    : (promotion.currentUsageCount / promotion.maxUsageCount) >= 0.8
                                        ? "bg-orange-500"
                                        : "bg-green-500"
                                    }`}
                                style={{
                                    width: `${Math.min((promotion.currentUsageCount / promotion.maxUsageCount) * 100, 100)}%`
                                }}
                            />
                        </div>
                        {promotion.currentUsageCount >= promotion.maxUsageCount && (
                            <p className="text-xs text-red-600 dark:text-red-400 mt-2 font-semibold">
                                ⚠️ Promotion épuisée
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
        </Link>
    )
}
